// The Titan Relic: how many moves a turn holds, and whose turn it is.
//
// **`Position.movesPerTurn` is a property of the game, not of a side**, and
// that is the whole reason this file exists. The Reactor Boosters never
// noticed: it
// writes `movesLeft`, the *current* side's remaining allowance, so its grant
// could not leak to anybody. Titan is permanent, so it has to be written
// somewhere that survives the clock passing — and the only such field is the
// one every side refills from.
//
// Caught by a test on the first run: granting it and playing two moves handed
// the enemy king two moves as well. A power that quietly upgrades the army it
// is meant to out-tempo is worse than no power.
//
// **The count is relics standing, derived from the board** (§26 A2). It was
// `run.titan`, a stored count on the run — right while the power was
// membership, and wrong the day it became a structure (F6), because a
// structure is captured (F5) and a stored count would need an event fired on
// every capture, wreck and demolition to stay true. Derived, the grant ends
// with the structure by construction: `hasPower`'s argument, applied to an
// amount — a capture *wrecks* the Relic (F5 drops its fabric as cobble, it
// never changes hands), so taking the enemy's Relic takes their move away
// rather than granting you one. It answers for **any side, not only
// `run.side`** — the spec's "under your control" is about who owns the
// structure, whoever that is. That is F4's reason `ticksPerSide` was indexed
// by side, arriving here.

import type { Side } from "../board";
import type { Position } from "../position";
import { type IndustryState, RELIC } from "../industry/state";
import { RULES } from "../rules";
import { poweredCount } from "./equipment";
import { armyLogisticsFor } from "./legion";

/**
 * How many Titan Relics this side has the function of, right now: a Pendant
 * borne whose Relic stands (§31 A0, `poweredCount`).
 */
export function relicsOf(industry: IndustryState, side: Side): number {
  return poweredCount(industry, side, RELIC);
}

/**
 * How many moves this side's turn holds.
 *
 * The bound is `maxMovesPerTurn` wherever the number comes from, so a stack of
 * relics and a scenario that opens high cannot add up past it.
 */
export function movesForSide(
  position: Position,
  industry: IndustryState,
  side: Side
): number {
  const own = relicsOf(industry, side) * RULES.titanMoves;
  return Math.min(RULES.maxMovesPerTurn, position.movesPerTurn + own);
}

/**
 * Fodder Moves this side's turn holds (§33 T1): its Power Moves × Army
 * Logistics, so a Titan Relic's move scales the fodder too ("scales well
 * with Titan Relics").
 */
export function fodderMovesFor(position: Position, industry: IndustryState, side: Side): number {
  return movesForSide(position, industry, side) * armyLogisticsFor(industry, side);
}
